import axios from 'axios';
import { Dialog } from 'evergreen-ui';
import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { EditUserIcon } from '../../../assets/EditUserIcon';
import { useFormik } from 'formik';
import { useQuery } from 'react-query';
import { editUser } from '../../../network';
import { FormGroup } from '../../../ui/form/FormGroup';
import { Input } from '../../../ui/form/Input';
import { Select } from '../../../ui/form/Select';
import { useOffices, usePeople, useTeams } from '../hooks';
import * as Yup from 'yup';
import { useToast } from '../../../utils/useToast';
import officesManagement from '../../../network/officesManagement';
import {
  listCustomFields,
  CustomFieldDefinition,
} from '../../../network/customFields';
import {
  PERSON_ROLE_OPTIONS,
  roleLabel,
  storedRoleFromListRole,
} from '../../../utils/roles';

// Country code mapping for Postcoder (used by the backend)
const countryCodeMapping: { [key: string]: string } = {
  GB: 'uk',
  DE: 'de',
  NL: 'nl',
  PL: 'pl',
  FR: 'fr',
  IE: 'ie',
  US: 'us',
  CA: 'ca',
  AU: 'au',
  NZ: 'nz',
  // Add more mappings as needed
};

type Props = {
  isOpen: boolean;
  onClose(): void;
  refetch(): void;
  user: any;
};

export const EditUserDialog = ({ isOpen, onClose, refetch, user }: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const { data: team_data } = useTeams();
  const { data: office_data } = useOffices();
  const { data: people_data } = usePeople();
  const { toast } = useToast();
  // The people list sends role labels, and "Unknown Role" for roles it does
  // not know (hr_admin, super_admin, ...). '' means the stored role is not
  // known here, so it must be left alone.
  const initialRole = storedRoleFromListRole(user?.role);
  const roleIsKnownOption = PERSON_ROLE_OPTIONS.some(
    ({ value }) => value === initialRole,
  );

  const { data: customFieldResponse } = useQuery({
    queryKey: ['custom-fields', 'person'],
    queryFn: () => listCustomFields({ scope: 'person', active: true }),
  });
  const customFields: CustomFieldDefinition[] = customFieldResponse?.data || [];

  // Address related states
  const [countries, setCountries] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [addressQuery, setAddressQuery] = useState<string>('');
  const [addressResults, setAddressResults] = useState<any[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  // Split the full name into first and last name
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  useEffect(() => {
    if (user?.full_name) {
      // Split the full name into parts
      const nameParts = user.full_name.split(' ');
      // The first part is the first name
      const first = nameParts[0] || '';
      // The rest is the last name (join with space if multiple parts)
      const last = nameParts.slice(1).join(' ') || '';

      setFirstName(first);
      setLastName(last);
    }
  }, [user]);

  // Fetch countries when component mounts
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await officesManagement.getCountries();
        setCountries(data);

        // If user has a country, set it as selected
        if (user?.address?.country) {
          setSelectedCountry(user.address.country);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, [user]);

  // Handle address search
  const handleAddressSearch = async (query: string) => {
    if (!selectedCountry || query.length < 3) {
      setAddressResults([]);
      return;
    }

    // Map the country code to the format expected by the API
    const mappedCountryCode =
      countryCodeMapping[selectedCountry] || selectedCountry.toLowerCase();

    setIsSearchingAddress(true);
    try {
      const results = await officesManagement.searchAddresses(
        mappedCountryCode,
        query,
      );
      setAddressResults(results);
    } catch (error) {
      console.error('Error searching addresses:', error);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  // Handle country change
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    setSelectedCountry(countryCode);
    formik.setFieldValue('address.country', countryCode);
  };

  // Handle address selection
  const handleAddressSelect = (address: any) => {
    formik.setFieldValue('address.country', selectedCountry);
    formik.setFieldValue(
      'address.line1',
      address.addressline1 || address.line1 || '',
    );
    formik.setFieldValue(
      'address.town',
      address.posttown || address.town || '',
    );
    formik.setFieldValue(
      'address.city',
      address.posttown || address.town || '',
    );
    formik.setFieldValue('address.postcode', address.postcode || '');
    setAddressResults([]);
    setAddressQuery('');
  };

  const handleCustomFieldChange = (fieldId: string, value: any) => {
    formik.setFieldValue(`custom_fields.${fieldId}`, value);
  };

  // Define validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    phone: Yup.string().matches(
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/,
      'Invalid phone number format',
    ),
  });

  const formik = useFormik({
    initialValues: {
      id: user?.id,
      firstName: firstName,
      lastName: lastName,
      role_name: user?.role_name || '',
      role: initialRole,
      team: user?.team?.id ? user.team.id : team_data?.[0]?.id ?? '',
      office: user?.office?.id ? user.office.id : office_data?.[0]?.id ?? '',
      manager: user?.manager?.id ? user.manager.id : '',
      phone: user?.phone && user.phone !== 'n/a' ? user.phone : '',
      address: {
        country: user?.address?.country || '',
        line1: user?.address?.line1 || '',
        town: user?.address?.town || '',
        city: user?.address?.city || '',
        postcode: user?.address?.postcode || '',
      },
      custom_fields: user?.custom_fields || {},
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsEditing(true);

        // Use destructuring to create a new object without firstName and lastName
        const { firstName, lastName, role, ...apiSubmitData } = values;

        // Map firstName and lastName to first_name and last_name for the API
        const submitData = {
          ...apiSubmitData,
          first_name: firstName,
          last_name: lastName,
          team: values.team, // team is now always an ID string or ''
          custom_fields: values.custom_fields,
          ...roleChange(role),
        };

        await editUser(submitData);
        setIsEditing(false);
        toast({
          variant: 'success',
          title: 'User successfully updated.',
        });
        closeDialog();
        refetch();
      } catch (error) {
        console.error(error);
        setIsEditing(false);
        toast({
          variant: 'error',
          title: 'Failed to update user.',
        });
      }
    },
  });

  // Only send `role` when the user picked a different one, so saving other
  // details never rewrites the stored role.
  function roleChange(role: string): { role?: string } {
    return role && role !== initialRole ? { role } : {};
  }

  // Direct submission function to bypass potential form handling issues
  const handleDirectSubmit = async () => {
    try {
      setIsEditing(true);
      console.log('Starting direct submission');

      // Extract team ID properly
      let teamValue;
      if (typeof formik.values.team === 'object' && formik.values.team?.id) {
        teamValue = formik.values.team.id;
      } else {
        teamValue = formik.values.team || null;
      }

      // Prepare the data manually
      const submitData = {
        id: user.id,
        first_name: formik.values.firstName,
        last_name: formik.values.lastName,
        role_name: formik.values.role_name,
        team: teamValue,
        office: formik.values.office,
        manager: formik.values.manager,
        phone: formik.values.phone,
        address: {
          country: formik.values.address.country,
          line1: formik.values.address.line1,
          town: formik.values.address.town,
          city: formik.values.address.city,
          postcode: formik.values.address.postcode,
        },
        custom_fields: formik.values.custom_fields,
        ...roleChange(formik.values.role),
      };

      console.log('Submitting data directly:', submitData);

      // Make the API call directly
      const API_HOST = process.env.REACT_APP_API_HOST;
      const response = await axios.patch(
        `${API_HOST}/v1/people/${user.id}`,
        submitData,
      );

      console.log('Direct API response:', response.data);

      setIsEditing(false);
      toast({
        variant: 'success',
        title: 'User successfully updated.',
      });
      closeDialog();
      refetch();
    } catch (error) {
      console.error('Direct submission error:', error);
      setIsEditing(false);
      toast({
        variant: 'error',
        title: 'Failed to update user.',
      });
    }
  };

  function findError(
    fieldName: keyof typeof formik.initialValues,
  ): string | undefined {
    const error = formik.touched[fieldName]
      ? formik.errors[fieldName]
      : undefined;
    return typeof error === 'string' ? error : undefined;
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log('Form submission initiated');
    formik.handleSubmit(event);
  };

  function closeDialog() {
    formik.resetForm();
    onClose();
  }

  return (
    <Dialog
      isShown={isOpen}
      onCloseComplete={closeDialog}
      hasFooter={false}
      hasHeader={false}
    >
      <div className="flex flex-col">
        <div className="mb-4 flex flex-row justify-between pt-6">
          <div className="rounded-[10px] bg-[#FEE9E9] p-3 text-[#DC2626] shadow-sm">
            <EditUserIcon />
          </div>
          <XMarkIcon className="h-6 w-6 cursor-pointer" onClick={onClose} />
        </div>

        <div className="mb-5 flex flex-col gap-1">
          <div className="text-lg font-semibold">User Detail</div>
          <div className="text-sm font-normal text-[#475467]">
            Update the user's information and permissions as needed. Make sure
            all details are accurate before saving.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <FormGroup label="First Name" htmlFor="firstName-input">
            <Input
              id="firstName-input"
              type="text"
              name="firstName"
              error={findError('firstName')}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter first name"
            />
          </FormGroup>

          <FormGroup label="Last Name" htmlFor="lastName-input">
            <Input
              id="lastName-input"
              type="text"
              name="lastName"
              error={findError('lastName')}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter last name"
            />
          </FormGroup>

          <FormGroup label="Job Title" htmlFor="job_title-input">
            <Input
              id="job_title-input"
              type="text"
              name="role_name"
              value={formik.values.role_name}
              onChange={formik.handleChange}
              placeholder="Enter job title"
            />
          </FormGroup>
          <FormGroup label="Role" htmlFor="role-input">
            <Select
              id="role-input"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
            >
              {!initialRole && <option value="">Keep current role</option>}
              {initialRole && !roleIsKnownOption && (
                <option value={initialRole}>{roleLabel(initialRole)}</option>
              )}
              {PERSON_ROLE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup label="Office location" htmlFor="office-input">
            <Select
              id="office-input"
              name="office"
              value={formik.values.office}
              onChange={formik.handleChange}
            >
              {office_data?.map((office: any, index: number) => (
                <option key={index} value={office.id}>
                  {office.name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Team" htmlFor="team-input">
            <Select
              id="team-input"
              name="team"
              value={formik.values.team}
              onChange={formik.handleChange}
            >
              {team_data?.map((team: any, index: number) => (
                <option key={index} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Manager" htmlFor="manager-input">
            <Select
              id="manager-input"
              name="manager"
              value={formik.values.manager}
              onChange={formik.handleChange}
            >
              <option value="">None</option>
              {people_data?.data?.map((person: any, index: number) => (
                <option key={index} value={person.id}>
                  {person.full_name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Phone" htmlFor="phone-input">
            <Input
              id="phone-input"
              type="text"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
            />
          </FormGroup>

          {/* Address Fields */}
          <div className="border-t border-gray-200 pt-2">
            <div className="text-md mb-2 font-semibold">
              Address Information
            </div>
          </div>

          <FormGroup label="Country" htmlFor="country-input">
            <Select
              id="country-input"
              name="address.country"
              value={selectedCountry}
              onChange={handleCountryChange}
            >
              <option value="">Select a country</option>
              {countries.map((country: any, index: number) => {
                // Handle different country name structures safely
                let displayName = '';
                if (typeof country.name === 'string') {
                  displayName = country.name;
                } else if (country.name && typeof country.name === 'object') {
                  displayName =
                    country.name.common || country.name.official || '';
                }

                return (
                  <option key={index} value={country.cca2}>
                    {displayName}
                  </option>
                );
              })}
            </Select>
          </FormGroup>

          <FormGroup label="Address Search" htmlFor="address-search">
            <div className="relative">
              <Input
                id="address-search"
                type="text"
                value={addressQuery}
                onChange={(e) => {
                  setAddressQuery(e.target.value);
                  handleAddressSearch(e.target.value);
                }}
                placeholder="Enter postcode or street name"
                disabled={!selectedCountry}
              />

              {addressResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg">
                  <ul className="max-h-60 overflow-auto rounded-md py-1 text-base sm:text-sm">
                    {addressResults.map((address, index) => (
                      <li
                        key={index}
                        className="relative cursor-pointer select-none px-3 py-2 hover:bg-gray-100"
                        onClick={() => handleAddressSelect(address)}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            {address.addressline1 || address.line1 || ''}
                          </span>
                          <span className="text-sm text-gray-500">
                            {address.addressline2 || ''}
                            {address.addressline3
                              ? `, ${address.addressline3}`
                              : ''}
                          </span>
                          <span className="text-xs text-gray-400">
                            {address.posttown || address.town || ''}{' '}
                            {address.postcode || ''}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {isSearchingAddress && (
                <div className="absolute right-3 top-2">
                  <span className="loading loading-spinner loading-xs"></span>
                </div>
              )}
            </div>
          </FormGroup>

          <FormGroup label="Address Line 1" htmlFor="address-line1">
            <Input
              id="address-line1"
              type="text"
              name="address.line1"
              value={formik.values.address.line1}
              onChange={formik.handleChange}
              placeholder="Street address"
            />
          </FormGroup>

          <FormGroup label="Town" htmlFor="address-town">
            <Input
              id="address-town"
              type="text"
              name="address.town"
              value={formik.values.address.town}
              onChange={formik.handleChange}
              placeholder="Town"
            />
          </FormGroup>

          <FormGroup label="City" htmlFor="address-city">
            <Input
              id="address-city"
              type="text"
              name="address.city"
              value={formik.values.address.city}
              onChange={formik.handleChange}
              placeholder="Enter city"
            />
          </FormGroup>

          <FormGroup label="Zip/Postal Code" htmlFor="address-zipCode">
            <Input
              id="address-zipCode"
              type="text"
              name="address.postcode"
              value={formik.values.address.postcode}
              onChange={formik.handleChange}
              placeholder="Postal code"
            />
          </FormGroup>

          {customFields.length > 0 && (
            <div className="border-t border-gray-200 pt-2">
              <div className="text-md mb-2 font-semibold">Custom fields</div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {customFields.map((field) => {
                  const value = formik.values.custom_fields?.[field.system.id];

                  if (field.details.type === 'boolean') {
                    return (
                      <label
                        key={field.system.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(value)}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              field.system.id,
                              e.target.checked,
                            )
                          }
                          disabled={isEditing}
                        />
                        {field.details.name}
                      </label>
                    );
                  }

                  if (field.details.type === 'single_select') {
                    return (
                      <FormGroup
                        key={field.system.id}
                        label={field.details.name}
                      >
                        <Select
                          value={value || ''}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              field.system.id,
                              e.target.value,
                            )
                          }
                          disabled={isEditing}
                        >
                          <option value="">Select...</option>
                          {(field.details.options || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Select>
                      </FormGroup>
                    );
                  }

                  if (field.details.type === 'multi_select') {
                    const selected: string[] = Array.isArray(value)
                      ? value
                      : typeof value === 'string' && value
                      ? [value]
                      : [];
                    return (
                      <FormGroup
                        key={field.system.id}
                        label={field.details.name}
                      >
                        <Select
                          multiple
                          value={selected}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              field.system.id,
                              Array.from(e.target.selectedOptions).map(
                                (o) => o.value,
                              ),
                            )
                          }
                          disabled={isEditing}
                        >
                          {(field.details.options || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Select>
                      </FormGroup>
                    );
                  }

                  if (field.details.type === 'date') {
                    return (
                      <FormGroup
                        key={field.system.id}
                        label={field.details.name}
                      >
                        <Input
                          type="date"
                          value={value || ''}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              field.system.id,
                              e.target.value,
                            )
                          }
                          disabled={isEditing}
                        />
                      </FormGroup>
                    );
                  }

                  if (field.details.type === 'number') {
                    return (
                      <FormGroup
                        key={field.system.id}
                        label={field.details.name}
                      >
                        <Input
                          type="number"
                          value={value ?? ''}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              field.system.id,
                              e.target.value,
                            )
                          }
                          disabled={isEditing}
                        />
                      </FormGroup>
                    );
                  }

                  return (
                    <FormGroup key={field.system.id} label={field.details.name}>
                      <Input
                        type="text"
                        value={value || ''}
                        onChange={(e) =>
                          handleCustomFieldChange(
                            field.system.id,
                            e.target.value,
                          )
                        }
                        disabled={isEditing}
                      />
                    </FormGroup>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-row justify-between gap-3 pb-6 ">
            <button
              type="button"
              className="w-full rounded-lg border border-[#D0D5DD] bg-white px-[18px] py-[10px] text-base font-semibold text-[#344054] shadow-sm"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="w-full rounded-lg bg-[#DC2626] px-[18px] py-[10px] text-base font-semibold text-white shadow-sm disabled:bg-[#47546789]"
              disabled={isEditing}
              onClick={handleDirectSubmit}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};
